import React, { useEffect, useState, useRef } from 'react';
import { onAuthStateChanged, updateProfile } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import styles from './PerfilCliente.module.css';

import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export default function PerfilCliente() {
  const [user, setUser] = useState(null);
  const [formData, setFormData] = useState({
    displayName: '',
    email: '',
    fechaNacimiento: '',
    telefono: '',
    fechaRegistro: ''
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const originalData = useRef(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, usr => setUser(usr));
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const updateOnlineStatus = () => setIsOnline(navigator.onLine);
    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);
    return () => {
      window.removeEventListener('online', updateOnlineStatus);
      window.removeEventListener('offline', updateOnlineStatus);
    };
  }, []);

  useEffect(() => {
    async function fetchUserData() {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const docRef = doc(db, 'usuarios', user.uid);
        const docSnap = await getDoc(docRef);

        const basicData = {
          displayName: user.displayName || '',
          email: user.email || ''
        };

        if (docSnap.exists()) {
          const extraData = docSnap.data();
          const fechaNacimientoStr = extraData.fechaNacimiento || '';

          const merged = {
            ...basicData,
            fechaNacimiento: fechaNacimientoStr,
            telefono: extraData.telefono || '',
            fechaRegistro: extraData.fechaRegistro || ''
          };

          setFormData(merged);
          originalData.current = merged;
        } else {
          const fechaRegistro = new Date().toISOString();
          const newUserData = {
            ...basicData,
            fechaNacimiento: '',
            telefono: '',
            fechaRegistro
          };
          await setDoc(docRef, newUserData);
          setFormData(newUserData);
          originalData.current = newUserData;
        }
      } catch (error) {
        console.error('Error cargando datos:', error);
      } finally {
        setLoading(false);
      }
    }

    if (user && isOnline) {
      fetchUserData();
    } else if (user) {
      setFormData({
        displayName: user.displayName || '',
        email: user.email || '',
        fechaNacimiento: '',
        telefono: '',
        fechaRegistro: ''
      });
      setLoading(false);
    }
  }, [user, isOnline]);

  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const hasChanges = () => {
    if (!originalData.current) return false;
    const keysToCompare = ['displayName', 'fechaNacimiento', 'telefono'];
    return keysToCompare.some(key => (formData[key] || '').trim() !== (originalData.current[key] || '').trim());
  };

  const handleSave = async () => {
    if (!formData.displayName.trim()) {
      toast.error('El nombre no puede estar vacío');
      return;
    }
    if (!isOnline) {
      toast.warn('No puedes guardar sin conexión a internet.');
      return;
    }
    if (!hasChanges()) {
      toast.info('No hay cambios para guardar');
      return;
    }

    setSaving(true);
    try {
      if (formData.displayName !== user.displayName) {
        await updateProfile(user, { displayName: formData.displayName });
      }

      const userDocRef = doc(db, 'usuarios', user.uid);
      const updateData = {
        displayName: formData.displayName,
        fechaNacimiento: formData.fechaNacimiento,
        telefono: formData.telefono
      };

      await setDoc(userDocRef, updateData, { merge: true });
      originalData.current = { ...formData };
      toast.success('✅ Perfil actualizado con éxito');
    } catch (error) {
      console.error(error);
      toast.error('❌ Error al actualizar el perfil');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.loaderContainer}>
        <div className={styles.loader}></div>
        <p>Cargando perfil...</p>
      </div>
    );
  }

  return (
    <>
      <div className={styles.profileCentered}>
        {!isOnline && (
          <p style={{ color: 'orange' }}>
            ⚠️ Estás sin conexión. Algunos datos pueden no estar actualizados.
          </p>
        )}

        <div className={styles.twoColumnLayout}>
          <div>
            <label htmlFor="displayName">Nombre Completo:</label>
            <input
              id="displayName"
              name="displayName"
              value={formData.displayName}
              onChange={handleInputChange}
              disabled={saving}
              required
            />

            <label htmlFor="fechaNacimiento">Fecha de nacimiento:</label>
            <input
              id="fechaNacimiento"
              name="fechaNacimiento"
              type="date"
              value={formData.fechaNacimiento}
              onChange={handleInputChange}
              disabled={saving}
            />

            <label htmlFor="telefono">Teléfono:</label>
            <input
              id="telefono"
              name="telefono"
              type="tel"
              value={formData.telefono}
              onChange={handleInputChange}
              disabled={saving}
              placeholder="Ej: +34 600 123 456"
            />
          </div>

          <div>
            <label>Correo electrónico:</label>
            <input value={formData.email} disabled readOnly />

            <label>Fecha de registro:</label>
            <input
              value={
                formData.fechaRegistro
                  ? new Date(formData.fechaRegistro).toLocaleString()
                  : 'No disponible'
              }
              disabled
              readOnly
            />
          </div>
        </div>

        <button
          className={styles.saveButton}
          onClick={handleSave}
          disabled={saving || !isOnline || !hasChanges()}
          title={hasChanges() ? '' : 'No hay cambios para guardar'}
        >
          {saving ? 'Guardando...' : 'Guardar cambios'}
        </button>
      </div>

      <ToastContainer position="top-center" autoClose={3000} theme="colored" />
    </>
  );
}
